import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ChevronRight,
    Clock,
    File as FileIcon,
    FileType2,
    Folder,
    HardDrive,
    Loader2,
    Monitor,
    Search,
    Users,
    X
} from "@fg/icons";
import { driveApi, isPdf, type DriveItem } from "@/api/driveApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

// Drive dialogs for the PDF app: pick a PDF to open (Recent / My Drive /
// Shared with me / search) and Save As (name + target + a My Drive folder).

interface Crumb {
    id: string | null;
    name: string;
}

const MY_DRIVE: Crumb = { id: null, name: "My Drive" };
const SHARED_ROOT: Crumb = { id: null, name: "Shared with me" };

// ── Modal shell ──────────────────────────────────────────────────────

function Modal({
    title, onClose, children, footer, className
}: {
    title: string;
    onClose: () => void;
    children: ReactNode;
    footer: ReactNode;
    className?: string;
}) {
    const panelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={cn(
                    "flex max-h-[88vh] w-[640px] max-w-full flex-col overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl",
                    className
                )}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                {children}
                <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">{footer}</div>
            </div>
        </div>
    );
}

const btnPrimary =
    "rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50";
const btnGhost = "rounded-md px-3 py-1.5 text-sm hover:bg-accent";

// ── Shared list pieces ───────────────────────────────────────────────

function ItemGlyph({ item }: { item: DriveItem }) {
    if (item.kind === "Folder") return <Folder className="h-5 w-5 shrink-0 fill-amber-400/30 text-amber-500" aria-hidden />;
    if (isPdf(item)) return <FileType2 className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" aria-hidden />;
    return <FileIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden />;
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function Breadcrumbs({ trail, onJump }: { trail: Crumb[]; onJump: (index: number) => void }) {
    return (
        <nav aria-label="Folder path" className="flex min-w-0 flex-wrap items-center gap-0.5 text-sm">
            {trail.map((c, i) => (
                <span key={`${c.id ?? "root"}-${i}`} className="flex min-w-0 items-center gap-0.5">
                    {i > 0 ? <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden /> : null}
                    {i === trail.length - 1 ? (
                        <span className="truncate px-1 font-medium">{c.name}</span>
                    ) : (
                        <button type="button" onClick={() => onJump(i)} className="truncate rounded px-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                            {c.name}
                        </button>
                    )}
                </span>
            ))}
        </nav>
    );
}

function ListStatus({ children }: { children: ReactNode }) {
    return <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">{children}</div>;
}

const byKindThenName = (a: DriveItem, b: DriveItem) =>
    a.kind === b.kind ? a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }) : a.kind === "Folder" ? -1 : 1;

// ── Open from Drive ──────────────────────────────────────────────────

type Tab = "recent" | "mine" | "shared";

/**
 * Pick a file from Drive. Recent, My Drive and Shared with me (both with
 * folder navigation), plus search across mine and shared. PDFs only unless
 * "All files" is ticked.
 */
export function DrivePickerDialog({ onClose, onPick }: { onClose: () => void; onPick: (item: DriveItem) => void }) {
    const token = useAccessToken();
    const [tab, setTab] = useState<Tab>("recent");
    const [trail, setTrail] = useState<Crumb[]>([MY_DRIVE]);
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [allFiles, setAllFiles] = useState(false);
    const [selected, setSelected] = useState<DriveItem | null>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(query.trim()), 250);
        return () => window.clearTimeout(t);
    }, [query]);

    const searching = debounced.length > 0;
    const folderId = trail[trail.length - 1]?.id ?? null;
    // Shared tab at its root lists Shared with me; deeper, a shared folder.
    const listingShared = tab === "shared" && folderId === null;

    const recent = useQuery({
        queryKey: ["pdf-drive", "recent"],
        queryFn: () => driveApi.listRecent(50, token),
        enabled: Boolean(token) && tab === "recent" && !searching
    });
    const folder = useQuery({
        queryKey: ["pdf-drive", "items", folderId],
        queryFn: () => driveApi.listItems(folderId, token),
        enabled: Boolean(token) && !searching && (tab === "mine" || (tab === "shared" && folderId !== null))
    });
    const shared = useQuery({
        queryKey: ["pdf-drive", "shared"],
        queryFn: () => driveApi.sharedWithMe(token),
        enabled: Boolean(token) && !searching && listingShared
    });
    const search = useQuery({
        queryKey: ["pdf-drive", "search", debounced],
        queryFn: () => driveApi.search(debounced, 100, token),
        enabled: Boolean(token) && searching
    });

    const active = searching ? search : tab === "recent" ? recent : listingShared ? shared : folder;
    const items = useMemo(() => {
        const rows = (active.data ?? []).filter((i) => !i.isTrashed && (i.kind === "Folder" || allFiles || isPdf(i)));
        return tab === "recent" && !searching ? rows : [...rows].sort(byKindThenName);
    }, [active.data, allFiles, tab, searching]);
    const hiddenCount = (active.data ?? []).filter((i) => i.kind === "File" && !isPdf(i)).length;

    const switchTab = (next: Tab) => {
        setTab(next);
        setTrail([next === "shared" ? SHARED_ROOT : MY_DRIVE]);
        setSelected(null);
        setQuery("");
    };

    const enterFolder = (f: DriveItem) => {
        setSelected(null);
        if (searching || tab === "recent") {
            // From search: jump into the folder under the tab it belongs to.
            const nextTab: Tab = f.ownerEmail ? "shared" : "mine";
            setTab(nextTab);
            setTrail([nextTab === "shared" ? SHARED_ROOT : MY_DRIVE, { id: f.id, name: f.name }]);
            setQuery("");
            setDebounced("");
            return;
        }
        setTrail((t) => [...t, { id: f.id, name: f.name }]);
    };

    const choose = (item: DriveItem) => {
        if (item.kind === "Folder") enterFolder(item);
        else onPick(item);
    };

    const tabBtn = (id: Tab, label: string, Icon: typeof Clock) => (
        <button
            key={id}
            type="button"
            role="tab"
            aria-selected={!searching && tab === id}
            onClick={() => switchTab(id)}
            className={cn(
                "flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm",
                !searching && tab === id ? "border-primary font-medium text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
        >
            <Icon className="h-4 w-4" aria-hidden /> {label}
        </button>
    );

    return (
        <Modal
            title="Open from Drive"
            onClose={onClose}
            footer={
                <>
                    <label className="mr-auto flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                        <input type="checkbox" checked={allFiles} onChange={(e) => setAllFiles(e.target.checked)} className="h-4 w-4 accent-primary" />
                        All files
                    </label>
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                    <button type="button" disabled={!selected} onClick={() => selected && onPick(selected)} className={btnPrimary}>
                        Open
                    </button>
                </>
            }
        >
            <div className="space-y-2 border-b px-4 pt-3">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                        placeholder="Search Drive"
                        aria-label="Search Drive"
                        autoFocus
                        className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    />
                </div>
                <div role="tablist" aria-label="Drive locations" className="-mb-px flex gap-1">
                    {tabBtn("recent", "Recent", Clock)}
                    {tabBtn("mine", "My Drive", HardDrive)}
                    {tabBtn("shared", "Shared with me", Users)}
                </div>
            </div>
            {!searching && tab !== "recent" && trail.length > 0 ? (
                <div className="border-b px-4 py-2">
                    <Breadcrumbs trail={trail} onJump={(i) => { setTrail((t) => t.slice(0, i + 1)); setSelected(null); }} />
                </div>
            ) : null}
            <div className="min-h-[18rem] flex-1 overflow-y-auto" role="listbox" aria-label="Files">
                {active.isPending && active.fetchStatus !== "idle" ? (
                    <ListStatus><Loader2 className="h-5 w-5 animate-spin" /> Loading…</ListStatus>
                ) : active.isError ? (
                    <ListStatus><span className="text-destructive">{active.error.message}</span></ListStatus>
                ) : items.length === 0 ? (
                    <ListStatus>
                        <span>
                            {searching ? `Nothing matches “${debounced}”` : tab === "recent" ? "No recently opened files" : "This folder is empty"}
                            {!allFiles && hiddenCount > 0 ? " — no PDFs here" : ""}
                        </span>
                        {!allFiles && hiddenCount > 0 ? (
                            <button type="button" onClick={() => setAllFiles(true)} className="text-xs font-medium text-primary hover:underline">
                                Show all files ({hiddenCount})
                            </button>
                        ) : null}
                    </ListStatus>
                ) : (
                    <ul className="divide-y">
                        {items.map((item) => {
                            const isSel = selected?.id === item.id;
                            return (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={isSel}
                                        onClick={() => (item.kind === "Folder" ? enterFolder(item) : setSelected(item))}
                                        onDoubleClick={() => choose(item)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); choose(item); } }}
                                        className={cn(
                                            "flex w-full items-center gap-3 px-4 py-2 text-left text-sm",
                                            isSel ? "bg-primary/10 dark:bg-primary/20" : "hover:bg-accent"
                                        )}
                                    >
                                        <ItemGlyph item={item} />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-medium" title={item.name}>{item.name}</span>
                                            {item.ownerEmail ? (
                                                <span className="block truncate text-xs text-muted-foreground">Shared by {item.ownerEmail}</span>
                                            ) : null}
                                        </span>
                                        <span className="hidden w-28 shrink-0 text-right text-xs text-muted-foreground sm:block">
                                            {formatDate(tab === "recent" && !searching ? item.lastAccessedAt ?? item.updatedAt : item.updatedAt)}
                                        </span>
                                        <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                                            {item.kind === "File" ? formatSize(item.sizeBytes) : ""}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </Modal>
    );
}

// ── Folder browser (My Drive only — new files are filed under their owner) ──

function FolderBrowser({
    trail, onTrailChange
}: {
    trail: Crumb[];
    onTrailChange: (trail: Crumb[]) => void;
}) {
    const token = useAccessToken();
    const folderId = trail[trail.length - 1]?.id ?? null;
    const list = useQuery({
        queryKey: ["pdf-drive", "items", folderId],
        queryFn: () => driveApi.listItems(folderId, token),
        enabled: Boolean(token)
    });
    const folders = (list.data ?? []).filter((i) => i.kind === "Folder" && !i.isTrashed && !i.ownerEmail).sort(byKindThenName);

    return (
        <div className="overflow-hidden rounded-md border">
            <div className="border-b bg-muted/40 px-3 py-1.5">
                <Breadcrumbs trail={trail} onJump={(i) => onTrailChange(trail.slice(0, i + 1))} />
            </div>
            <div className="h-48 overflow-y-auto">
                {list.isPending ? (
                    <ListStatus><Loader2 className="h-4 w-4 animate-spin" /></ListStatus>
                ) : list.isError ? (
                    <ListStatus><span className="text-destructive">{list.error.message}</span></ListStatus>
                ) : folders.length === 0 ? (
                    <ListStatus>No folders here — it will be saved in “{trail[trail.length - 1]?.name ?? "My Drive"}”.</ListStatus>
                ) : (
                    <ul>
                        {folders.map((f) => (
                            <li key={f.id}>
                                <button
                                    type="button"
                                    onClick={() => onTrailChange([...trail, { id: f.id, name: f.name }])}
                                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                                >
                                    <ItemGlyph item={f} />
                                    <span className="min-w-0 flex-1 truncate">{f.name}</span>
                                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export interface SaveAsChoice {
    target: "drive" | "computer";
    /** File name without extension. */
    name: string;
    /** Drive folder (null = My Drive root). */
    folderId: string | null;
    folderName: string;
}

/**
 * Save As: file name, Drive or this computer, and — for Drive — the My Drive
 * folder to save into (starting at `startFolderId` when the open file is the
 * user's own).
 */
export function SaveAsDialog({
    defaultName, initialTarget, startFolderId, onClose, onConfirm
}: {
    defaultName: string;
    initialTarget: "drive" | "computer";
    startFolderId: string | null;
    onClose: () => void;
    onConfirm: (choice: SaveAsChoice) => void;
}) {
    const token = useAccessToken();
    const [target, setTarget] = useState(initialTarget);
    const [name, setName] = useState(defaultName);
    const [trail, setTrail] = useState<Crumb[]>([MY_DRIVE]);

    // Start in the open file's own folder.
    useEffect(() => {
        if (!startFolderId || !token) return;
        let cancelled = false;
        driveApi.ancestors(startFolderId, token).then(
            (chain) => {
                if (!cancelled) setTrail([MY_DRIVE, ...chain.map((c) => ({ id: c.id, name: c.name }))]);
            },
            () => { /* fall back to My Drive */ }
        );
        return () => { cancelled = true; };
        // Only when the dialog opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const here = trail[trail.length - 1] ?? MY_DRIVE;
    const clean = name.trim().replace(/\.pdf$/i, "");
    const submit = () => {
        if (!clean) return;
        onConfirm({ target, name: clean, folderId: here.id, folderName: here.name });
    };

    const targetBtn = (id: "drive" | "computer", label: string, Icon: typeof Monitor) => (
        <button
            type="button"
            onClick={() => setTarget(id)}
            aria-pressed={target === id}
            className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm",
                target === id ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"
            )}
        >
            <Icon className="h-4 w-4" aria-hidden /> {label}
        </button>
    );

    return (
        <Modal
            title="Save as"
            onClose={onClose}
            className="w-[520px]"
            footer={
                <>
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                    <button type="button" onClick={submit} disabled={!clean} className={btnPrimary}>
                        {target === "drive" ? `Save to ${here.name}` : "Download"}
                    </button>
                </>
            }
        >
            <form
                className="flex min-h-0 flex-col gap-3 overflow-y-auto p-4"
                onSubmit={(e) => { e.preventDefault(); submit(); }}
            >
                <div>
                    <label htmlFor="save-as-name" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">File name</label>
                    <div className="flex items-center gap-2">
                        <input
                            id="save-as-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        />
                        <span className="text-sm text-muted-foreground">.pdf</span>
                    </div>
                </div>
                <div>
                    <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Save to</span>
                    <div className="flex gap-2">
                        {targetBtn("drive", "Drive", HardDrive)}
                        {targetBtn("computer", "This computer", Monitor)}
                    </div>
                </div>
                {target === "drive" ? (
                    <div>
                        <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Folder</span>
                        <FolderBrowser trail={trail} onTrailChange={setTrail} />
                    </div>
                ) : null}
            </form>
        </Modal>
    );
}

/** From this computer / From Drive. */
export function OpenChoiceDialog({
    onClose, onPickComputer, onPickDrive
}: { onClose: () => void; onPickComputer: () => void; onPickDrive: () => void }) {
    const choice = (onClick: () => void, Icon: typeof Monitor, title: string, hint: string) => (
        <button type="button" onClick={onClick} className="flex items-center gap-3 rounded-md border p-3 text-left hover:bg-accent">
            <Icon className="h-6 w-6 shrink-0 text-primary" aria-hidden />
            <span className="flex-1">
                <span className="block text-sm font-medium">{title}</span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
        </button>
    );
    return (
        <Modal
            title="Open PDF"
            onClose={onClose}
            className="w-[400px]"
            footer={<button type="button" onClick={onClose} className={btnGhost}>Cancel</button>}
        >
            <div className="flex flex-col gap-2 p-4">
                {choice(onPickDrive, HardDrive, "From Drive", "Your files, recent files and files shared with you")}
                {choice(onPickComputer, Monitor, "From this computer", "Pick a .pdf file")}
            </div>
        </Modal>
    );
}
